import { Accordion, Flex, Input, Text, Title } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useDocumentTitle } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';

import styles from './styles.module.scss';
import i18n from '@common/i18n/config';
import { useSearchParams } from 'react-router-dom';

const values = [
  {
    value: 'vially-score',
    label: 'faqItems.label1',
    description: 'faqItems.description1',
  },
  {
    value: 'compliance',
    label: 'faqItems.label2',
    description: 'faqItems.description2',
  },
  {
    value: 'install-widget',
    label: 'faqItems.label3',
    description: 'faqItems.description3',
  },
  {
    value: 'configure',
    label: 'faqItems.label4',
    description: 'faqItems.description4',
  },
  {
    value: 'user-reports',
    label: 'faqItems.label5',
    description: 'faqItems.description5',
  },
  {
    value: 'automated-audit',
    label: 'faqItems.label6',
    description: 'faqItems.description6',
  },
  {
    value: 'set-up-automation',
    label: 'faqItems.label7',
    description: 'faqItems.description7',
  },
  {
    value: 'update-my-personal-information',
    label: 'faqItems.label8',
    description: 'faqItems.description8',
  },
  {
    value: 'What-is-digital-accessibility',
    label: 'faqItems.label9',
    description: 'faqItems.description9',
  },
  {
    value: 'WCAG-Accessibility',
    label: 'faqItems.label10',
    description: 'faqItems.description10',
  },
  {
    value: 'types-Guidelines',
    label: 'faqItems.label11',
    description: 'faqItems.description11',
  },
  {
    value: 'WCAG-compliance',
    label: 'faqItems.label12',
    description: 'faqItems.description12',
  },
  {
    value: 'functionalities-insight',
    label: 'faqItems.label13',
    description: 'faqItems.description13',
  },
  {
    value: 'acc-accessibility',
    label: 'faqItems.label14',
    description: 'faqItems.description14',
  },
  {
    value: 'help-acc',
    label: 'faqItems.label15',
    description: 'faqItems.description15',
  },
  {
    value: 'page-number',
    label: 'faqItems.label16',
    description: 'faqItems.description16',
  },
  {
    value: 'website-scanning',
    label: 'faqItems.label17',
    description: 'faqItems.description17',
  },
  {
    value: 'first-result',
    label: 'faqItems.label18',
    description: 'faqItems.description18',
  },
  {
    value: 'multiple-pages',
    label: 'faqItems.label19',
    description: 'faqItems.description19',
  }
];

const FAQPage = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab')
  const [t] = useTranslation('faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredValues, setFilteredValues] = useState(values);
  const inputRef = useRef<any>(null);
  useDocumentTitle(t('title'));
  const installRef = useRef<HTMLButtonElement>(null);
  const reportsRef = useRef<HTMLButtonElement>(null);

  const setValues = (): void => {
    const newStrings = values.map((e) => ({ ...e, label: t(e.label), description: t(e.description) }));
    setFilteredValues(newStrings);
  };

  useEffect(() => {
    setValues();

    i18n.on('languageChanged', setValues);

    return () => {
      i18n.off('languageChanged', setValues);
    };
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setValues();
    } else {
      const filtered = filteredValues.filter((item) => item.label.toLowerCase().includes(searchQuery.toLowerCase()));
      const newFiltered = filtered.map((e) => ({ ...e, label: t(e.label), description: t(e.description) }));
      setFilteredValues(newFiltered);
    }
  }, [searchQuery]);

  useEffect(() => {
    const focusOnInstall = Object.fromEntries(new URLSearchParams(location.search)).focusOnInstall;
    const focusOnReports = Object.fromEntries(new URLSearchParams(location.search)).focusOnReports;

    if (installRef.current && focusOnInstall) {
      installRef.current.focus();
    }
    if (reportsRef.current && focusOnReports) {
      reportsRef.current.focus();
    }
  }, []);

  const handleSearchChange = (event: any): void => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    if (!tab && inputRef.current) {
        inputRef.current.focus();
    }
}, []);

  return (
    <Flex style={{ flexGrow: 1 }} direction={'column'} bg={'#F6FAFA'} my={0} px={24} py={24}>
      <Title id={'mainContent'} color={'#264547'}>
        {t('title')}
      </Title>
      <Input ref={inputRef}  py={10} placeholder={`${t('searchTopic')}`} value={searchQuery} onChange={handleSearchChange} />
      <Accordion className={styles.accordion}  defaultValue={tab ?? "" } >
        {filteredValues.map((item) => (
          <Accordion.Item key={item.value} value={item.value}  >
            <Accordion.Control
              autoFocus={ tab == item.value }
              {...(item.value === 'install-widget'
                ? { ref: installRef }
                : { ...(item.value === 'user-reports' ? { ref: reportsRef } : {}) })}>
              {item.label}
            </Accordion.Control>
            <Accordion.Panel>{
              
              item.value == 'vially-score' ? <ViallyScoreFAQ/> : item.description
              
            }</Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Flex>
  );
};

export default FAQPage;




function ViallyScoreFAQ() {
  return (
    <div style={{ maxWidth : '100%'}}> 
      <Title order={1} color="#264547" >What is the Vially Score?</Title>
      <p>The Vially score tells you how well a website meets specific success criteria (rules or guidelines it should follow). It’s based on how many of these criteria were not met (failed) and how many issues, on average, were found per webpage.</p>
      <p>You can't get a perfect 100% score with automated testing alone because an automated tool doesn't check every single criterion.</p>
      <Title order={2} color="#264547">How the Score is Calculated:</Title>
      <ul>
        <li><strong>Issues per Webpage:</strong> If a webpage has a lot of issues:
          <ul>
            <li>20 or more issues per page lowers the score by 5%.</li>
            <li>40 or more issues lowers it by 10%.</li>
            <li>70 or more issues lowers it by 15%.</li>
          </ul>
        </li>
        <li><strong>Success Criteria Tested:</strong> The formula looks at how many success criteria were actually tested, how many were not tested, and how many were failed. The final score is calculated based on how many criteria were tested and passed, but also adjusted (lowered) if there are a lot of issues per webpage.</li>
      </ul>
      <p><strong>No Negative Scores:</strong> To ensure the score isn’t too low, there's a rule that the score won't drop below a certain level, like 10%, even if the site performs very poorly.</p>
      <p>In Simple Math, the score is calculated as <code>(Total Passed Criteria / Total Criteria) * 100 - Penalty</code>, but if the score drops too low, it won’t go below a minimum threshold (e.g., 10%).</p>
    </div>
    
  );
}